import { Plot } from 'd3plus';
import {
  splitAndTrim,
  parseValue,
  formatYAxis,
  formatTooltip,
  fetchChartData,
} from './chart-utils';

/**
 * Initialize all line charts on the page
 * Finds chart containers, fetches CSV data, and renders D3Plus line charts
 */
export const initLineChart = () => {
  const charts = document.querySelectorAll(
    '.line-chart-wrapper .chart-container',
  );

  if (charts.length === 0) {
    return;
  }

  for (const chart of charts) {
    if (chart instanceof HTMLElement) {
      renderLineChart(chart);
    }
  }
};

/**
 * Render a single line chart
 * @param {HTMLElement} chartContainer - The chart container element with data attributes
 */
const renderLineChart = (chartContainer) => {
  const chartInner = chartContainer.querySelector('.chart-inner');

  if (!chartInner || !(chartInner instanceof HTMLElement)) {
    console.error('Chart inner container not found');
    return;
  }

  const dataset = chartContainer.dataset;
  const chartFile = dataset.chartFile;
  const xColumn = dataset.xColumn;
  const yColumnsStr = dataset.yColumns;
  const colorsStr = dataset.colors;

  if (!chartFile || !xColumn || !yColumnsStr || !colorsStr) {
    console.error('Missing required data attributes');
    chartInner.innerHTML = '<p>Missing chart configuration</p>';
    return;
  }

  const yColumns = splitAndTrim(yColumnsStr);
  const colors = splitAndTrim(colorsStr);
  const yFormat = dataset.yFormat || 'percentage';

  fetchChartData(
    chartInner,
    chartFile,
    '.line-chart-wrapper',
    (parsedData) => {
      // Transform data for D3Plus
      const chartData = transformDataToLongFormat(
        parsedData,
        xColumn,
        yColumns,
      );

      // Create and render D3Plus Plot
      new Plot()
        .data(chartData)
        .select(chartInner)
        .groupBy('id')
        .shape('Line')
        .x('x')
        .y('y')
        .shapeConfig({
          Line: {
            stroke: (d) => colors[yColumns.indexOf(d.id)] || colors[0],
            strokeWidth: 2,
          },
        })
        .yConfig({
          tickFormat: (value) => formatYAxis(value, yFormat),
          gridConfig: {
            stroke: '#e0e0e0',
          },
        })
        .xConfig({
          gridConfig: {
            stroke: 'transparent',
          },
          shapeConfig: {
            labelConfig: {
              fontColor: '#1D2328',
              fontSize: 12,
            },
          },
          barConfig: {
            stroke: 'transparent',
          },
          tickFormat: (d) => {
            // Only return year label for first occurrence of each year
            const dataPoint = chartData.find((item) => item.x === d);
            const result =
              dataPoint && dataPoint.ShowYearLabel ? dataPoint.Year : '';
            return result;
          },
        })
        .legendPosition('bottom')
        .legendConfig({
          shapeConfig: {
            labelConfig: {
              fontColor: '#1D2328',
            },
          },
        })
        .tooltipConfig({
          tbody: [['Value', (d) => formatTooltip(d.Value, yFormat)]],
        })
        .render();
    },
    undefined,
    false,
  ); // Disable responsive - D3Plus handles it internally
};

/**
 * Transform CSV data from wide format to long format for D3Plus
 * @param {Array} parsedData - Parsed CSV data
 * @param {string} xColumn - Name of the X-axis column (typically Date)
 * @param {Array<string>} yColumns - Array of Y-axis column names
 * @returns {Array} Transformed data in long format
 */
const transformDataToLongFormat = (parsedData, xColumn, yColumns) => {
  const chartData = [];
  const yearsWithLabels = new Set();

  // First pass: determine which rows should show year labels
  const rowYearMap = [];
  for (let i = 0; i < parsedData.length; i++) {
    const row = parsedData[i];
    const dateStr = row[xColumn];
    // Handle both date strings (1/1/2007) and year strings (2004)
    const year = dateStr.includes('/')
      ? new Date(dateStr).getFullYear()
      : parseInt(dateStr, 10);

    rowYearMap.push({
      year,
      showLabel: !yearsWithLabels.has(year),
      index: i,
    });

    if (!yearsWithLabels.has(year)) {
      yearsWithLabels.add(year);
    }
  }

  // Second pass: create chart data with unique x values
  for (let i = 0; i < parsedData.length; i++) {
    const row = parsedData[i];
    const dateStr = row[xColumn];
    const date = dateStr.includes('/')
      ? new Date(dateStr)
      : new Date(parseInt(dateStr, 10), 0, 1);
    const yearInfo = rowYearMap[i];

    for (const column of yColumns) {
      const rawValue = row[column];

      if (rawValue && rawValue !== '') {
        const value = parseValue(rawValue);

        chartData.push({
          x: i, // Use row index for unique x values
          xLabel: dateStr, // Keep original for display
          y: value,
          id: column,
          Series: column,
          Year: yearInfo.year,
          ShowYearLabel: yearInfo.showLabel,
          Date: date,
          Value: value,
        });
      }
    }
  }

  return chartData;
};
